import React from 'react';
import {
  Box,
  Fab,
  FormControl,
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Tooltip,
  Typography,
} from '@mui/material';
import FilterRowMultiSelect from '@/components/Filter/FilterRow/FilterRowMultiSelect';
import FilterRowSearchableMultiSelect from '@/components/Filter/FilterRow/FilterRowSearchableMultiSelect';
import FilterRowDate from '@/components/Filter/FilterRow/FilterRowDate.tsx';
import FilterRowTextField from '@/components/Filter/FilterRow/FilterRowTextField';
import FilterRowSelect from '@/components/Filter/FilterRow/FilterRowSelect';
import FilterRowBooleanToggle from '@/components/Filter/FilterRow/FilterRowBooleanToggle';
import { FilterDefinition } from '@lambda-layer/models/FilterDefinition/FilterDefinition.ts';
import StarIcon from '@mui/icons-material/Star';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import {
  FilterRowSelection,
  FilterRowSelectionValue,
  RelationalOperator,
} from '@lambda-layer/types/filter.ts';
import { ControlType } from '@lambda-layer/models/FilterDefinition/FilterDefinitionFields.ts';
import {
  getOperatorDisplayLabel,
  getOperatorTooltip,
} from '@/utils/operatorDisplay';

export interface Option {
  value: string | null;
  label: string | null;
  count: number;
}

export interface FilterRowProps {
  isLoading?: boolean;
  filterRowSelection: FilterRowSelection;
  onChange: (filterRowSelection: FilterRowSelection) => void;
  options: Option[];
  recordCount?: number;
  onFocusChange?: () => void;
  disabled?: boolean;
  unselectedValue?: string;
  filterDefinition: FilterDefinition;
  displaySaveFilters?: boolean;
  onSaveFilterClick?: () => void;
  autoFocus?: boolean;
}

const UNSPECIFIED_LABEL = 'Unspecified';

// Helper function to convert a value to string (or UNSPECIFIED_LABEL if empty, null, or undefined)
const normalizeValue = (value: FilterRowSelectionValue | undefined): string =>
  value === undefined || value === null || value.toString().trim() === ''
    ? UNSPECIFIED_LABEL
    : value.toString();

// Updated to use normalizeValue for proper conversion
const getValueOrUnspecified = (val: FilterRowSelection['value'][0]) =>
  normalizeValue(val);

export const getLabelFromOptionValues = (
  labelMapping: Record<string, string> | undefined,
  values: FilterRowSelection['value'],
  flagMapping?: Record<string, { friendlyName: string; description?: string }>
) => {
  const unspecifiedValues = values.filter(
    value => value === undefined || value === null
  );
  const specifiedValues = values.filter(
    value => value !== undefined && value !== null
  );

  const unspecifiedLabels = unspecifiedValues.map(() => UNSPECIFIED_LABEL);
  const specifiedLabels = specifiedValues.map(value => {
    const valueStr = value.toString();

    // First check flag mapping (takes precedence)
    if (flagMapping && flagMapping[valueStr]) {
      return flagMapping[valueStr].friendlyName;
    }

    // Then check label mapping
    if (labelMapping && labelMapping[valueStr]) {
      return getValueOrUnspecified(labelMapping[valueStr]);
    }

    // Fall back to original value
    return getValueOrUnspecified(value);
  });

  return [...unspecifiedLabels, ...specifiedLabels].join(', ');
};

// Updated to normalize both the option value and the filter value during comparison.
export const doOptionsContainValue = (
  options: Option[],
  val: FilterRowSelection['value']
) => {
  return val.some(v =>
    options.some(option => normalizeValue(option.value) === normalizeValue(v))
  );
};

const FilterRow: React.FC<FilterRowProps> = ({
  isLoading = false,
  filterRowSelection,
  onChange,
  options = [],
  onFocusChange,
  recordCount,
  disabled = false,
  filterDefinition,
  displaySaveFilters,
  onSaveFilterClick,
  autoFocus = false,
}) => {
  const handleOperatorChange = (
    event: SelectChangeEvent<RelationalOperator>
  ) => {
    const relationalOperator = event.target.value as RelationalOperator;
    const updatedValue = { ...filterRowSelection, relationalOperator };

    // For non-array multi-select fields, clear values when switching away from equal
    if (
      filterDefinition.controlType === ControlType.MULTI_SELECT &&
      !filterDefinition.isArrayField &&
      relationalOperator !== RelationalOperator.EQUAL
    ) {
      onChange({ ...updatedValue, value: [] });
    }

    onChange(updatedValue);
  };

  const selectId = `${filterDefinition.label?.toLowerCase().replace(/\s+/g, '-')}-select`;
  const selectCountId = `${selectId}-count`;

  // Use availableOperators if provided, otherwise default to all operators
  const operators =
    filterDefinition.availableOperators || Object.values(RelationalOperator);

  const FieldTooltip = (props: {
    title?: string;
    children: React.ReactElement;
  }) => (
    <>
      {props.title ? (
        <Tooltip title={props.title}>{props.children}</Tooltip>
      ) : (
        props.children
      )}
    </>
  );

  return filterDefinition.label &&
    filterDefinition.controlType &&
    !filterDefinition.isAdvancedFilter ? (
    <Grid
      container
      alignItems="center"
      spacing={2}
      mb={1}
      sx={{
        color: filterDefinition.isAdvancedFilter === false ? 'red' : 'inherit',
      }}
    >
      <Grid item xs={3}>
        <FieldTooltip title={filterDefinition.toolTip}>
          <Typography>
            {filterDefinition.label}
            {filterDefinition.toolTip && (
              <HelpOutlineIcon
                color={'secondary'}
                sx={{
                  fontSize: '0.85em',
                  marginBottom: '0.175em',
                  marginLeft: '0.175em',
                  verticalAlign: 'middle',
                }}
              />
            )}
          </Typography>
        </FieldTooltip>
      </Grid>
      <Grid item xs={1}>
        <Typography component={'div'}>
          {filterDefinition.controlType === ControlType.BOOLEAN_TOGGLE ? (
            // Hide operator display for boolean toggles since it's just a toggle
            <span></span>
          ) : filterDefinition.isDatabaseField &&
            (filterDefinition.databaseMetadata?.dataType === 'number' ||
              filterDefinition.isArrayField) &&
            operators.length > 1 ? (
            <Select
              value={filterRowSelection.relationalOperator}
              onChange={handleOperatorChange}
              variant="standard"
              size="small"
              disabled={disabled}
            >
              {operators.map(op => (
                <MenuItem key={op} value={op}>
                  <Tooltip title={getOperatorTooltip(op)} placement="right">
                    <span>{getOperatorDisplayLabel(op)}</span>
                  </Tooltip>
                </MenuItem>
              ))}
            </Select>
          ) : operators.length === 1 ? (
            <Tooltip title={getOperatorTooltip(operators[0])} placement="right">
              <span>{getOperatorDisplayLabel(operators[0])}</span>
            </Tooltip>
          ) : (
            <Tooltip
              title={getOperatorTooltip(filterRowSelection.relationalOperator)}
              placement="right"
            >
              <span>
                {getOperatorDisplayLabel(filterRowSelection.relationalOperator)}
              </span>
            </Tooltip>
          )}
        </Typography>
      </Grid>
      <Grid item xs={6}>
        {displaySaveFilters && (
          <Box
            sx={{
              position: 'absolute',
              transform: 'translateX(-45%) translateY(-45%)',
              zIndex: 100,
            }}
          >
            <Tooltip title={'Save selected filters'}>
              <Fab
                color="primary"
                aria-label="Save Filter"
                sx={{ scale: '0.4' }}
                onClick={onSaveFilterClick}
              >
                <StarIcon />
              </Fab>
            </Tooltip>
          </Box>
        )}
        <FormControl fullWidth size="small" disabled={disabled}>
          {filterDefinition.controlType === ControlType.DATE && (
            <FilterRowDate
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              onChange={onChange}
              onFocusChange={onFocusChange}
              isRange={false}
            />
          )}
          {filterDefinition.controlType === ControlType.DATE_RANGE && (
            <FilterRowDate
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              onChange={onChange}
              onFocusChange={onFocusChange}
              isRange={true}
            />
          )}
          {filterDefinition.controlType === ControlType.MULTI_SELECT && (
            <FilterRowMultiSelect
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              options={options}
              onChange={onChange}
              onFocusChange={onFocusChange}
              filterDefinition={filterDefinition}
              isLoading={isLoading}
            />
          )}
          {filterDefinition.controlType ===
            ControlType.SEARCHABLE_MULTI_SELECT && (
            <FilterRowSearchableMultiSelect
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              options={options}
              onChange={onChange}
              onFocusChange={onFocusChange}
              filterDefinition={filterDefinition}
              isLoading={isLoading}
            />
          )}
          {filterDefinition.controlType === ControlType.SELECT && (
            <FilterRowSelect
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              label={filterDefinition.label}
              options={options}
              onChange={onChange}
              onFocusChange={onFocusChange}
              labelMapping={filterDefinition.labelMapping}
              isLoading={isLoading}
              isMetaField={!filterDefinition.isDatabaseField}
            />
          )}
          {filterDefinition.controlType === ControlType.TEXT && (
            <FilterRowTextField
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              onChange={onChange}
              onFocusChange={onFocusChange}
              disabled={disabled}
              isNumeric={
                filterDefinition.databaseMetadata?.dataType === 'number'
              }
            />
          )}
          {filterDefinition.controlType === ControlType.BOOLEAN_TOGGLE && (
            <FilterRowBooleanToggle
              autoFocus={autoFocus}
              filterRowSelection={filterRowSelection}
              onChange={onChange}
              onFocusChange={onFocusChange}
              filterDefinition={filterDefinition}
            />
          )}
        </FormControl>
      </Grid>
      {/* Only render record count if this field is not advanced */}
      <Grid item xs={2}>
        <Typography data-testid={selectCountId}>
          {recordCount?.toLocaleString()}
        </Typography>
      </Grid>
    </Grid>
  ) : null;
};

export default FilterRow;
